import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Brain, Eye, Clock, Lock, MessageCircle } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/5219983032313?text=' + encodeURIComponent('Hola ABBA Massage, me gustaría recibir información sobre una regresión a vidas pasadas con Priscila Vallejo.');

const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1757066033606-b9552acb2dd0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxoeXBub3NpcyUyMHRoZXJhcHklMjBzZXNzaW9uJTIwcmVsYXhlZCUyMHdvbWFuJTIwZXllcyUyMGNsb3NlZCUyMHNlcmVuZXxlbnwwfHx8fDE3NzI4Mjk3NTF8MA&ixlib=rb-4.1.0&q=85',
  therapy: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/p81db8rj_SALA%20DE%20ESTAR%20ABBA%20ESTUDIO.JPG',
};

const FAQS = [
  {
    es: '¿En qué idioma se realizan las sesiones?',
    en: 'What language are sessions conducted in?',
    aes: 'Las sesiones de regresión con Priscila Vallejo se realizan únicamente en español.',
    aen: 'Regression sessions with Priscila Vallejo are conducted in Spanish only.'
  },
  {
    es: '¿Qué sucede durante una sesión?',
    en: 'What happens during a session?',
    aes: 'Antes de tu cita recibirás un cuestionario y un audio de preparación con ejercicios de relajación e inducción. Durante la sesión conversarás con Priscila sobre tus respuestas, resolverás tus dudas y realizarán el proceso de regresión. Al finalizar, conversarán sobre tu experiencia para ayudarte a integrarla. Después recibirás un cuestionario para escribir sobre lo vivido y continuar la integración.',
    aen: 'Before your appointment, you will receive a questionnaire and a preparation audio with relaxation and induction exercises. During the session, you will discuss your answers with Priscila, ask questions and go through the regression process. A closing conversation helps you integrate your experience. Afterwards, you will receive a questionnaire to write about what you experienced and continue the integration process.'
  },
  {
    es: '¿Voy a perder el control?',
    en: 'Will I lose control?',
    aes: 'No. Permaneces consciente y puedes comunicarte, hacer preguntas o detener la sesión en cualquier momento.',
    aen: 'No. You remain aware and can communicate, ask questions or stop the session at any time.'
  },
  {
    es: '¿La sesión es confidencial?',
    en: 'Is the session confidential?',
    aes: 'Sí. La sesión se realiza en un espacio privado y la información compartida se trata con respeto y confidencialidad.',
    aen: 'Yes. The session takes place in a private space and the information shared is treated respectfully and confidentially.'
  },
  {
    es: '¿Sustituye la atención médica o psicológica?',
    en: 'Does it replace medical or psychological care?',
    aes: 'No. La regresión se ofrece como una práctica complementaria de bienestar y no sustituye diagnóstico, tratamiento médico, psicoterapia ni atención de emergencia.',
    aen: 'No. Regression is offered as a complementary wellness practice and does not replace diagnosis, medical treatment, psychotherapy or emergency care.'
  }
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

export const Regressions = () => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const canonicalUrl = 'https://abbamassage.com/hipnosis';
  const title = isSpanish
    ? 'Regresiones e Hipnosis en Cancún | ABBA Massage'
    : 'Past Life Regression with Priscila Vallejo | ABBA Massage';
  const description = isSpanish
    ? 'Sesiones individuales de regresión a vidas pasadas con Priscila Vallejo. Parte de la técnica está basada en el trabajo de Brian Weiss. Online o presenciales en Cancún, únicamente en español y para mayores de 21 años.'
    : 'Individual past life regression sessions with Priscila Vallejo. Part of the technique is based on the work of Brian Weiss. Online or in person in Cancun, conducted in Spanish only, for adults over 21.';

  useEffect(() => {
    document.title = title;

    const setMeta = (attribute, key, value) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', IMAGES.hero);
    setMeta('property', 'og:locale', isSpanish ? 'es_MX' : 'en_US');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', IMAGES.hero);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
  }, [description, isSpanish, title]);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': canonicalUrl + '#service',
        name: isSpanish ? 'Regresiones e Hipnosis con Priscila Vallejo' : 'Past Life Regression with Priscila Vallejo',
        url: canonicalUrl,
        description,
        provider: { '@type': 'Person', name: 'Priscila Vallejo' },
        areaServed: { '@type': 'City', name: 'Cancún' },
        availableChannel: [
          { '@type': 'ServiceChannel', serviceUrl: canonicalUrl, availableLanguage: 'Spanish' },
          { '@type': 'ServiceChannel', serviceLocation: { '@type': 'Place', name: 'Cancún' }, availableLanguage: 'Spanish' }
        ]
      },
      {
        '@type': 'BreadcrumbList',
        '@id': canonicalUrl + '#breadcrumb',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: isSpanish ? 'Inicio' : 'Home', item: 'https://abbamassage.com/' },
          { '@type': 'ListItem', position: 2, name: isSpanish ? 'Regresiones e Hipnosis' : 'Past Life Regression', item: canonicalUrl }
        ]
      },
      {
        '@type': 'FAQPage',
        '@id': canonicalUrl + '#faq',
        mainEntity: FAQS.map((faq) => ({
          '@type': 'Question',
          name: isSpanish ? faq.es : faq.en,
          acceptedAnswer: { '@type': 'Answer', text: isSpanish ? faq.aes : faq.aen }
        }))
      }
    ]
  };

  const services = [
    {
      icon: Brain,
      name: isSpanish ? 'Sesiones online' : 'Online sessions',
      description: isSpanish
        ? 'Puedes participar desde cualquier lugar. Reserva tres horas y prepara un espacio tranquilo, privado y con buena conexión a internet.'
        : 'Join from wherever you are. Set aside three hours and prepare a quiet, private space with a reliable internet connection.'
    },
    {
      icon: Eye,
      name: isSpanish ? 'Sesiones presenciales en Cancún' : 'In-person sessions in Cancun',
      description: isSpanish
        ? 'Una sesión individual de tres horas con Priscila. La ubicación y disponibilidad se confirman al agendar con ABBA.'
        : 'A three-hour individual session with Priscila. Location and availability are confirmed when booking through ABBA.'
    }
  ];

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <div data-testid="regressions-page">
        <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.hero}
              alt={isSpanish ? 'Relajación para una sesión de regresión' : 'Relaxation for a regression session'}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl">
              <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
                {isSpanish ? 'Con Priscila Vallejo · Sesiones en español' : 'With Priscila Vallejo · Spanish-only sessions'}
              </motion.p>
              <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
                {isSpanish ? 'Regresiones e Hipnosis' : 'Past Life Regression'}
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/85 mb-8 font-['Manrope']">
                {isSpanish
                  ? 'Un proceso individual guiado por Priscila Vallejo para explorar tu mundo interior con cuidado y respeto. Su enfoque integra distintas herramientas; parte de la técnica está basada en el trabajo del Dr. Brian Weiss.'
                  : 'An individual process guided by Priscila Vallejo to explore your inner world with care and respect. Her approach integrates different tools; part of the technique is based on the work of Dr. Brian Weiss.'}
              </motion.p>
              <motion.div variants={fadeInUp}>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    <MessageCircle className="mr-2 w-5 h-5" />
                    {isSpanish ? 'Solicitar información' : 'Request information'}
                  </Button>
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        <nav aria-label={isSpanish ? 'Migas de pan' : 'Breadcrumb'} className="border-b border-gray-100 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 text-sm font-['Manrope'] text-[#5A5A5A]">
            <Link to="/" className="hover:text-[#A91B60]">{isSpanish ? 'Inicio' : 'Home'}</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">{isSpanish ? 'Regresiones e Hipnosis' : 'Past Life Regression'}</span>
          </div>
        </nav>

        <section className="py-20 md:py-24 px-6 bg-white" data-testid="priscila-profile">
          <div className="max-w-6xl mx-auto grid md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-10 md:gap-14 items-center">
            <img
              src="/images/priscila-vallejo-portrait.jpg"
              alt="Priscila Vallejo"
              width={1086}
              height={1448}
              loading="lazy"
              className="w-full max-w-md mx-auto rounded-2xl shadow-lg"
            />
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-3">{isSpanish ? 'Terapia de Regresiones' : 'Regression Therapy'}</h2>
              <p className="text-xl italic text-[#A91B60] mb-6">{isSpanish ? 'Un viaje de autoconocimiento' : 'A journey of self-discovery'}</p>
              <div className="space-y-5 text-lg text-[#5A5A5A] font-['Manrope'] leading-relaxed">
                <p>{isSpanish
                  ? <><strong>Priscila Vallejo</strong> comenzó su camino en Negocios Internacionales y se desarrolló en la administración y las ventas. Con el tiempo, la vida le mostró su verdadera vocación: acompañar a otras personas en la exploración de su mundo interior.</>
                  : <><strong>Priscila Vallejo</strong> began her path in International Business and developed her career in administration and sales. Over time, life revealed her true calling: accompanying others in exploring their inner world.</>}</p>
                <p>{isSpanish
                  ? <>Decidió seguir ese llamado y se formó en regresiones a vidas pasadas en <strong>ArmoniosaMenthe</strong>, con una técnica inspirada en el trabajo del <strong>Dr. Brian Weiss</strong>.</>
                  : <>She decided to follow that calling and trained in past life regression at <strong>ArmoniosaMenthe</strong>, with a technique inspired by the work of <strong>Dr. Brian Weiss</strong>.</>}</p>
                <p>{isSpanish
                  ? <>Actualmente cursa la maestría en Psicoterapia en Hipnosis Ericksoniana en el <strong>CEM, Centro Ericksoniano de México</strong>.</>
                  : <>She is currently pursuing a master’s degree in Psychotherapy in Ericksonian Hypnosis at <strong>CEM, Centro Ericksoniano de México</strong>.</>}</p>
                <p>{isSpanish
                  ? 'Cada sesión se vive con escucha atenta, presencia y respeto por el proceso de cada persona.'
                  : 'Each session is approached with attentive listening, presence and respect for each person’s process.'}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-6 bg-[#F8F6F3]">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-5">
              {isSpanish ? '¿Qué puedes esperar?' : 'What can you expect?'}
            </h2>
            <p className="text-lg text-[#5A5A5A] font-['Manrope'] leading-relaxed max-w-3xl mx-auto">
              {isSpanish
                ? 'El acompañamiento comienza antes de tu cita y continúa después de la sesión. El proceso se guía paso a paso en un entorno confidencial. Permaneces consciente y puedes detenerte cuando lo necesites.'
                : 'The process begins before your appointment and continues after your session. You are guided step by step in a confidential setting. You remain aware and may stop whenever you need to.'}
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-10 text-left" data-testid="regression-process">
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-[#A91B60] mb-3">{isSpanish ? 'Antes de tu cita' : 'Before your appointment'}</h3>
                <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">{isSpanish
                  ? 'Recibirás un cuestionario y un audio de preparación con ejercicios de relajación e inducción.'
                  : 'You will receive a questionnaire and a preparation audio with relaxation and induction exercises.'}</p>
              </div>
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-[#A91B60] mb-3">{isSpanish ? 'Durante la sesión' : 'During your session'}</h3>
                <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">{isSpanish
                  ? 'Primero conversarás con Priscila sobre tus respuestas al cuestionario y resolverás tus dudas. Después realizarán el proceso de regresión. Al finalizar, tendrán una conversación sobre lo que experimentaste para ayudarte a integrarlo. La sesión dura aproximadamente tres horas.'
                  : 'First, you will discuss your questionnaire answers with Priscila and ask any questions. Then you will go through the regression process. A closing conversation about what you experienced helps you integrate it. The session lasts approximately three hours.'}</p>
              </div>
              <div className="bg-white rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-[#A91B60] mb-3">{isSpanish ? 'Después de tu sesión' : 'After your session'}</h3>
                <p className="text-[#5A5A5A] font-['Manrope'] leading-relaxed">{isSpanish
                  ? 'Recibirás un cuestionario para escribir sobre tu experiencia y continuar el proceso de integración.'
                  : 'You will receive a questionnaire to write about your experience and continue the integration process.'}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5 mt-10 max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl p-6"><Clock className="w-7 h-7 text-[#A91B60] mx-auto mb-3" /><strong>{isSpanish ? '3 horas por sesión' : '3 hours per session'}</strong></div>
              <div className="bg-white rounded-2xl p-6"><Lock className="w-7 h-7 text-[#A91B60] mx-auto mb-3" /><strong>{isSpanish ? 'Individual · Mayores de 21 años' : 'Individual · Adults over 21'}</strong></div>
            </div>
          </div>
        </section>

        <section className="py-24 md:py-28 px-6" data-testid="regression-services">
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div key={index} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
                <div className="w-16 h-16 rounded-full bg-[#F8F6F3] flex items-center justify-center mb-6">
                  <service.icon className="w-8 h-8 text-[#A91B60]" />
                </div>
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1C1C1C] mb-4 tracking-tight">{service.name}</h2>
                <p className="text-lg text-[#5A5A5A] leading-relaxed font-['Manrope']">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-20 md:py-24 bg-[#F8F6F3] px-6">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <img
              src={IMAGES.therapy}
              alt={isSpanish ? 'Espacio privado de ABBA Massage en Cancún' : 'ABBA Massage private space in Cancun'}
              className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
            />
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C] mb-6">
                {isSpanish ? 'Un espacio privado y respetuoso' : 'A private, respectful space'}
              </h2>
              <p className="text-lg text-[#5A5A5A] leading-relaxed mb-6 font-['Manrope']">
                {isSpanish
                  ? 'Las sesiones se realizan únicamente en español. Son individuales, con cita previa, online o presenciales en Cancún. Están dirigidas exclusivamente a mayores de 21 años. Antes de agendar, conversa con ABBA por WhatsApp para confirmar modalidad, ubicación, disponibilidad e inversión.'
                  : 'Sessions are conducted in Spanish only. Individual appointments are available online or in person in Cancun, exclusively for adults over 21. Before booking, contact ABBA through WhatsApp to confirm the format, location, availability and price.'}
              </p>
              <p className="text-sm text-[#5A5A5A] leading-relaxed font-['Manrope']">
                {isSpanish
                  ? 'Servicio complementario de bienestar. No sustituye atención médica, psicológica o psiquiátrica.'
                  : 'Complementary wellness service. It does not replace medical, psychological or psychiatric care.'}
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 px-6 bg-white" data-testid="regressions-faq">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm uppercase tracking-[0.2em] text-[#A91B60] mb-3 font-['Manrope'] font-semibold">{isSpanish ? 'Preguntas frecuentes' : 'Frequently asked questions'}</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1C1C1C]">{isSpanish ? 'Antes de tu sesión' : 'Before your session'}</h2>
            </div>
            <div className="space-y-6">
              {FAQS.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 pb-6">
                  <h3 className="font-semibold text-lg mb-2">{isSpanish ? faq.es : faq.en}</h3>
                  <p className="text-[#5A5A5A] leading-relaxed">{isSpanish ? faq.aes : faq.aen}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#A91B60] px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-5">{isSpanish ? 'Conversemos antes de agendar' : 'Let’s talk before you book'}</h2>
            <p className="text-white/85 font-['Manrope'] mb-8">{isSpanish ? 'Cuéntanos qué estás buscando y resolveremos tus preguntas por WhatsApp.' : 'Tell us what you are looking for and we will answer your questions through WhatsApp.'}</p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-white hover:bg-gray-100 text-[#A91B60] rounded-full px-9 py-6 font-['Manrope'] font-semibold">
                {isSpanish ? 'Contactar por WhatsApp' : 'Contact through WhatsApp'}<ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default Regressions;

